/**
 * What the cell says about a password, in words the person who typed it can act on.
 *
 * Two screens set a password — signup and the end of a reset — and both meet the same server rule:
 * security.md §2.1 specifies "minimum 12 chars, no composition rules, every candidate checked
 * against the breached corpus" via HIBP k-anonymity, and GoTrue refuses one with
 * `422 weak_password`. Signup shipped that verdict to the caller first (#170); this module is the
 * part of it that is not about signup, so the reset screen cannot quietly grow a second, worse copy
 * of the same sentences.
 *
 * A weak-password refusal is a fact about the string the caller just typed. It says nothing about
 * whether an address has an account, so it is never the membership oracle the uniform auth messages
 * exist to prevent (security.md §11).
 */

/** Reads a property off a value that may be anything, including null. */
export function property(value: unknown, name: string): unknown {
  return typeof value === 'object' && value !== null && name in value
    ? (value as Record<string, unknown>)[name]
    : undefined
}

/**
 * GoTrue's `WeakPasswordReasons`, as clauses. auth-js carries them on `AuthWeakPasswordError.reasons`
 * and may carry NONE — `fetch.ts` falls back to `data.weak_password?.reasons || []` — so the empty
 * case is a real one and gets its own sentence rather than an empty "because".
 */
const WEAK_PASSWORD_CLAUSE: Readonly<Record<string, string>> = {
  pwned: 'it appears in a public breach corpus',
  length: 'it is too short',
  characters: 'it does not use the mix of characters this cell requires',
}

export const PASSWORD_ADVICE = 'Several unrelated words you have not used anywhere else work well.'

/** The message for a `weak_password` refusal, or `null` when the failure is something else. */
export function weakPasswordMessage(error: unknown): string | null {
  if (property(error, 'code') !== 'weak_password') return null
  const raw = property(error, 'reasons')
  const clauses = (Array.isArray(raw) ? raw : [])
    .map((reason) => (typeof reason === 'string' ? WEAK_PASSWORD_CLAUSE[reason] : undefined))
    .filter((clause): clause is string => clause !== undefined)
  return clauses.length === 0
    ? `That password was refused. ${PASSWORD_ADVICE}`
    : `That password was refused because ${clauses.join(' and ')}. ${PASSWORD_ADVICE}`
}

/** The client-side floor. The real rule is the server's; this only saves an obvious round trip. */
export const MIN_PASSWORD_LENGTH = 12

/** The hint under every field that sets a password. It must not deny a rule the server enforces. */
export const PASSWORD_HINT =
  'At least 12 characters. No capital or symbol rules — but it is checked against known breached passwords, so do not reuse one.'

export const TOO_SHORT_MESSAGE = 'Passwords must be at least 12 characters.'

/** True while the caller has typed something that the server would refuse on length alone. */
export function tooShort(password: string): boolean {
  return password.length > 0 && [...password].length < MIN_PASSWORD_LENGTH
}
